import React, { useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Container, Row } from 'reactstrap';
import { Alignment, Button, DateInput, Icon, Item, Label, SelectInput, TextArea } from 'tacho-ui';
import { ApplicationState } from '../../../state/ApplicationState';
import { DispatchType } from '../../../state/configureStore';
import { TachoModal } from '../../components/TachoModal';
import { TachoEditModalTitle } from '../../components/TachoModal/TachoEditTitle';
import { AddLicenseTerm, defaultAddLicenseTerm } from '../../../core/domainModels/licenseTerms/AddLicenseTerm';
import { defaultLicenseTerm } from '../../../core/domainModels/licenseTerms/LicenseTerm';
import { addLicenseTermThunk, fetchLicenseTermsThunk } from '../../../state/licenseTerms/reducer';
import config from '../../../config';
import { createDateFromTimeZone, dateToStringFormat } from '../../../core/helpers/moment';
import { LicenseTypeEnum } from '../../../core/domainModels/licences/LicenseType';
import { getKeyFromEnum } from '../../../core/helpers/enumUtils';
import useLicenseTypesOptions from '../../hooks/useLicenseTypesOptions';
interface AddLicenseTermModalProps {
    isOpen: boolean;
    onToggle: () => void;
}
export const AddLicenseTermModal = (props: AddLicenseTermModalProps) => {
    const { formatMessage } = useIntl();
    const dispatch = useDispatch<DispatchType>();
    const [data, setData] = useState<AddLicenseTerm>(defaultAddLicenseTerm);
    const onSubmit = () => {
        dispatch(addLicenseTermThunk(data))
            .unwrap()
            .then(success => {
                if (success) {
                    dispatch(fetchLicenseTermsThunk());

                    setData(defaultAddLicenseTerm);
                    props.onToggle();
                }
            })
            .catch(err => {
                console.log(err);
            });
    };
    const addPending = useSelector((state: ApplicationState) => state.distributors.addDistributorPending);
    const addError = useSelector((state: ApplicationState) => state.licenseTerms.addLicenseTermError);
    const uploadPercent = useSelector((state: ApplicationState) => state.licenseTerms.uploadPercent);
    const uploadLoaded = useSelector((state: ApplicationState) => state.licenseTerms.uploadLoaded);
    const uploadTotal = useSelector((state: ApplicationState) => state.licenseTerms.uploadTotal);
    const licenseTypesOptions = useLicenseTypesOptions();
    const fileInput = useRef<HTMLInputElement>(null);
    const handleAddFiles = (event: React.ChangeEvent<HTMLInputElement>): void => {
        if (event.target.files) {
            const file = event.target.files[0];
            if (file) {
                setData({ ...data, file: file });
            }
        }

        if (fileInput.current?.value) {
            fileInput.current.value = '';
        }
    };

    return (
        <TachoModal
            size={'lg'}
            height={'auto'}
            isOpen={props.isOpen}
            onToggle={() => {
                setData(defaultLicenseTerm);
                props.onToggle();
            }}
            onSubmit={onSubmit}
            title={<TachoEditModalTitle titleToTranslation="licenseTermsAdd" icon={Icon.User} />}
            submitLabelToTranslate="commonAdd"
            submitIcon={Icon.Add}
            isPending={addPending}
            errorToTranslate={addError}
            disableSubmitButton={
                !data.notes ||
                !data.startDate ||
                !data.endDate ||
                !data.licenseType ||
                !data.file ||
                data.startDate >= data.endDate
            }
        >
            <Container fluid className="d-flex flex-column px-4 gap-3">
                <Row>
                    <Label content={data.file && data.file ? data.file.name : formatMessage({ id: 'fileNotChoosen' })} />
                    {addPending && uploadTotal > 0 && (
                        <span>
                            {formatMessage({ id: 'uploading' })} {Math.round(uploadLoaded / (1024 * 1024))} /{' '}
                            {Math.round(uploadTotal / (1024 * 1024))} MB - {uploadPercent}%
                        </span>
                    )}
                    {!addPending && (
                        <Button
                            theme="primary"
                            text={formatMessage({ id: 'versionsAddFile' })}
                            onClick={() => fileInput.current?.click()}
                            icon={Icon.OppenFile}
                            className="chooseFileButton"
                        />
                    )}
                </Row>
                <Row>
                    <SelectInput
                        label={formatMessage({ id: 'licencesLicenseType' })}
                        value={licenseTypesOptions.find(item => item.id === LicenseTypeEnum[data.licenseType])?.label || ''}
                        options={licenseTypesOptions}
                        onChange={value =>
                            setData({
                                ...data,
                                licenseType: getKeyFromEnum(Object.entries(LicenseTypeEnum), (value as Item).id),
                            })
                        }
                        disabled={addPending}
                    />
                </Row>
                <Row>
                    <DateInput
                        label={formatMessage({ id: 'licenseTermsStartDate' })}
                        value={dateToStringFormat(data?.startDate)}
                        onChange={value => setData({ ...data, startDate: createDateFromTimeZone(value) })}
                        locale={config.language}
                        disabled={addPending}
                    />
                </Row>
                <Row>
                    <DateInput
                        label={formatMessage({ id: 'licenseTermsEndDate' })}
                        value={dateToStringFormat(data?.endDate)}
                        onChange={value => setData({ ...data, endDate: createDateFromTimeZone(value) })}
                        locale={config.language}
                        disabled={addPending}
                    />
                </Row>
                <Row>
                    <TextArea
                        labelLength={-1}
                        label={formatMessage({ id: 'licenseTermsNotes' })}
                        align={Alignment.flexStart}
                        value={data.notes}
                        onChange={value => setData({ ...data, notes: value as string })}
                    />
                </Row>
                <input
                    type="file"
                    className="d-none"
                    accept={'.pdf'}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => handleAddFiles(event)}
                    ref={fileInput}
                />
            </Container>
        </TachoModal>
    );
};
